import { sumBy } from '@app/shared/helpers';
import classNames from 'classnames';
import { FC } from 'react';
import styles from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart.module.scss';
import {
  PerYearCapitalStructureChartInstrument,
  PerYearCapitalStructureChartStructure,
} from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart-types';
import { InstrumentBlockKey } from '@app/shared/models/contracts/enums/shared-enums';
import { NET_DEBT_RANK } from '../../pwerm2-widgets/total-equity/constants';
import PerYearCapitalStructureChartInstrumentBlock from './PerYearCapitalStructureChartInstrumentBlock';
import { PerYearCapitalStructureChartInstrumentStack } from './PerYearCapitalStructureChartInstrumentStack';
import { getTotalEquityModalBlockClasses } from '../totalEquityChartHelper';

const PARENT_CLASSNAME = 'modal-equity-chart';

interface PerYearCapitalStructureChartProps {
  structure: PerYearCapitalStructureChartStructure | null;
  selectedBlock: Nullable<string>;
  setSelectedBlock: (block: string | null) => void;
}

const PerYearCapitalStructureModalChart: FC<PerYearCapitalStructureChartProps> = ({
  structure,
  selectedBlock,
  setSelectedBlock,
}): JSX.Element | null => {
  if (!structure) {
    return null;
  }

  const netDebtRank = NET_DEBT_RANK;
  const netDebt = structure.ranks.find((rank) => rank.rank === netDebtRank)?.totalProceeds ?? 0;
  const absoluteNetDebt = Math.abs(netDebt);

  // this will be true if there is no net debt, and instead there is net cash (-ve debt)
  const isNetCash = netDebt < 0;

  const renderInstrumentBlock = (
    instrument: PerYearCapitalStructureChartInstrument,
    index: number
  ): JSX.Element => {
    const noBlocksSelected = !selectedBlock;
    const thisBlockSelected = Boolean(selectedBlock) && selectedBlock === instrument.id;

    return (
      <PerYearCapitalStructureChartInstrumentBlock
        key={'instrument-block-component' + index + instrument.id}
        instrument={instrument}
        className={getTotalEquityModalBlockClasses(noBlocksSelected, thisBlockSelected, instrument)}
        onClick={() => setSelectedBlock(instrument.id)}
      />
    );
  };

  const totalEquity = sumBy(
    structure.ranks.filter((rank) => rank.rank !== netDebtRank).flatMap((x) => x.instruments),
    (x) => x.totalProceeds
  );

  const equityHeight = (totalEquity / (totalEquity + absoluteNetDebt)) * 100;
  const debtHeight = (absoluteNetDebt / (totalEquity + absoluteNetDebt)) * 100;

  /*
    Positive net debt displays above the axis line.
    Negative net debt (i.e. net cash) displays below the axis line.
    The --with-positive-debt and --for-positive-debt modifiers move the bottom border
    from the base of the instrument block to the base of the net debt block
  */
  return (
    <div className={styles[PARENT_CLASSNAME]}>
      <div
        className={classNames(styles[`${PARENT_CLASSNAME}__chart`], {
          [styles[`${PARENT_CLASSNAME}__chart--with-positive-debt`]]: !isNetCash,
        })}
        style={{
          height: equityHeight + '%',
        }}>
        <div className={styles[`${PARENT_CLASSNAME}__chart-inner`]}>
          <div
            className={classNames(
              styles[`${PARENT_CLASSNAME}__column`],
              styles[`${PARENT_CLASSNAME}__column--expanded`]
            )}>
            <PerYearCapitalStructureChartInstrumentStack
              key={'instrument-stack' + selectedBlock}
              structure={structure}
              instrumentBlockRenderer={renderInstrumentBlock}
              isExpanded
            />
          </div>
        </div>
      </div>
      <div
        className={classNames(
          styles[`${PARENT_CLASSNAME}__chart`],
          styles[`${PARENT_CLASSNAME}__chart--alternate`]
        )}
        style={{
          height: debtHeight + '%',
        }}>
        <div className={styles[`${PARENT_CLASSNAME}__chart-inner`]}>
          <div
            className={classNames(
              styles[`${PARENT_CLASSNAME}__column`],
              styles[`${PARENT_CLASSNAME}__column--expanded`],
              styles[`${PARENT_CLASSNAME}__column--within-modal`]
            )}
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              setSelectedBlock(InstrumentBlockKey.NetDebtId);
            }}>
            <div
              className={classNames(
                styles[`${PARENT_CLASSNAME}__debt-column`],
                styles[`${PARENT_CLASSNAME}__debt-column--expanded`],
                {
                  [styles[`${PARENT_CLASSNAME}__debt-column--for-positive-debt`]]: !isNetCash,
                  [styles['modal-instrument']]:
                    !selectedBlock || selectedBlock === InstrumentBlockKey.NetDebtId,
                  [styles['modal-instrument--faded']]:
                    selectedBlock && selectedBlock !== InstrumentBlockKey.NetDebtId,
                }
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerYearCapitalStructureModalChart;
